// src/modules/app-views/crm/service.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import { API, apiUrl } from 'constants/Api';
import { clone, generateUidV4, generateMongoObjectId } from 'utils/helpers';
import { mergeUrlParamsQuery } from 'utils/urlUtils';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'crm';

// *******************************************************************************
// BOARD PARAMETERS (read/write via universal parameters)
// *******************************************************************************
function getBoardGroups() {
  return appService.getModulePreferences({ moduleId: MODULE_ID, category: 'boardGroups' }) || [];
}

// Returns ALL boards including archived/removed (for internal mutation operations)
function getAllBoardsRaw() {
  return appService.getModulePreferences({ moduleId: MODULE_ID, category: 'boards' }) || [];
}

// Returns only active boards (excludes archived and removed)
function getBoards() {
  return getAllBoardsRaw().filter(b => !b.removedAt && !b.archivedAt);
}

function getTemperatures() {
  return appService.getModulePreferences({ moduleId: MODULE_ID, category: 'temperatures' }) || [];
}

function getSources() {
  return appService.getModulePreferences({ moduleId: MODULE_ID, category: 'sources' }) || [];
}

function getBoardByUid(boardUid) {
  const boards = getBoards();
  return boards.find(b => b.uid === boardUid) || null;
}

function getColumnsByBoardUid(boardUid) {
  const board = getBoardByUid(boardUid);
  return board?.columns || [];
}

async function saveBoardGroups(boardGroups) {
  await appService.updateModulePreferences({
    moduleId: MODULE_ID,
    category: 'boardGroups',
    data: boardGroups,
  });
}

async function saveBoards(boards) {
  await appService.updateModulePreferences({
    moduleId: MODULE_ID,
    category: 'boards',
    data: boards,
  });
}

async function saveTemperatures(temperatures) {
  await appService.updateModulePreferences({
    moduleId: MODULE_ID,
    category: 'temperatures',
    data: temperatures,
  });
}

async function saveSources(sources) {
  await appService.updateModulePreferences({
    moduleId: MODULE_ID,
    category: 'sources',
    data: sources,
  });
}

// *******************************************************************************
// BOARD GROUP CRUD
// *******************************************************************************
async function addBoardGroup(group) {
  const groups = clone(getBoardGroups());
  const uid = generateUidV4();
  groups.push({ uid, ...group });
  await saveBoardGroups(groups);
  return uid;
}

async function updateBoardGroupTitle(groupUid, title) {
  const groups = clone(getBoardGroups());
  const idx = groups.findIndex(g => g.uid === groupUid);
  if (idx >= 0) {
    groups[idx].label = title;
    await saveBoardGroups(groups);
  }
}

async function removeBoardGroup(groupUid) {
  const groups = clone(getBoardGroups());
  const filtered = groups.filter(g => g.uid !== groupUid);
  await saveBoardGroups(filtered);
}

// *******************************************************************************
// BOARD CRUD
// *******************************************************************************
async function addBoard(board) {
  const boards = clone(getAllBoardsRaw());
  const uid = generateUidV4();
  boards.push({ uid, columns: [], ...board });
  await saveBoards(boards);
  return uid;
}

async function updateBoard(boardUid, data) {
  const boards = clone(getAllBoardsRaw());
  const idx = boards.findIndex(b => b.uid === boardUid);
  if (idx >= 0) {
    // Prevent overwriting boardGroupUid with undefined/null — always preserve the existing value
    const patch = { ...data };
    if (patch.boardGroupUid == null) delete patch.boardGroupUid;
    boards[idx] = { ...boards[idx], ...patch };
    await saveBoards(boards);
  }
}

// Soft-delete: marks board with removedAt + removedByAccountId (not physically removed)
async function removeBoard(boardUid, accountId) {
  const boards = clone(getAllBoardsRaw());
  const idx = boards.findIndex(b => b.uid === boardUid);
  if (idx >= 0) {
    boards[idx].removedAt = new Date().toISOString();
    boards[idx].removedByAccountId = accountId || null;
    await saveBoards(boards);
  }
}

// Archive: marks board with archivedAt + archivedByAccountId
async function archiveBoard(boardUid, accountId) {
  const boards = clone(getAllBoardsRaw());
  const idx = boards.findIndex(b => b.uid === boardUid);
  if (idx >= 0) {
    boards[idx].archivedAt = new Date().toISOString();
    boards[idx].archivedByAccountId = accountId || null;
    await saveBoards(boards);
  }
}

// Checks if a board has any active CRM documents (opportunities in the kanban)
async function checkBoardHasActiveDocuments(boardUid) {
  try {
    const data = await fetchBoardView(boardUid);
    const totalCards = (data || []).reduce((sum, group) => sum + (group.cards?.length || 0), 0);
    return totalCards > 0;
  } catch {
    // On error, assume documents exist (safe default to block deletion)
    return true;
  }
}

// *******************************************************************************
// COLUMN CRUD
// *******************************************************************************
async function addColumn(boardUid, column) {
  const boards = clone(getAllBoardsRaw());
  const idx = boards.findIndex(b => b.uid === boardUid);
  if (idx >= 0) {
    // Accept a pre-generated uid for optimistic UI updates; fall back to a new one
    const uid = column?.uid || generateMongoObjectId();
    const order = (boards[idx].columns?.length || 0) + 1;
    const { uid: _ignored, ...columnData } = column || {};
    boards[idx].columns = [...(boards[idx].columns || []), { uid, order, ...columnData }];
    await saveBoards(boards);
    return uid;
  }
  return null;
}

async function updateColumn(boardUid, columnUid, data) {
  const boards = clone(getAllBoardsRaw());
  const bIdx = boards.findIndex(b => b.uid === boardUid);
  if (bIdx >= 0) {
    const cIdx = boards[bIdx].columns.findIndex(c => c.uid === columnUid);
    if (cIdx >= 0) {
      boards[bIdx].columns[cIdx] = { ...boards[bIdx].columns[cIdx], ...data };
      await saveBoards(boards);
    }
  }
}

async function removeColumn(boardUid, columnUid) {
  const boards = clone(getAllBoardsRaw());
  const bIdx = boards.findIndex(b => b.uid === boardUid);
  if (bIdx >= 0) {
    boards[bIdx].columns = boards[bIdx].columns.filter(c => c.uid !== columnUid);
    await saveBoards(boards);
  }
}

// *******************************************************************************
// API CALLS (backend crm collection)
// *******************************************************************************

// Create a new CRM opportunity document
async function addOpportunity(data) {
  const url = API.CRM.URL.replace('/:_id?', '');
  const [result] = await axiosService.post({ url, data, returnAsObject: true });
  return result;
}

// Fetch board view - backend aggregates crm documents grouped by columnUid
// Each column uid in parameters matches documents where columnUid === column.uid
async function fetchBoardView(boardUid, consultantIds) {
  const url = apiUrl.build({
    url: API.CRM.BOARD,
    params: { boardUid },
  });

  const query = consultantIds?.length ? `?consultantIds=${JSON.stringify(consultantIds)}` : '';

  const [data] = await axiosService.get({
    url: `${url}${query}`,
    returnAsObject: false,
  });

  return data || [];
}

// Move opportunity to a different column - updates columnUid on the crm document
async function moveOpportunity(_id, body) {
  const url = apiUrl.build({
    url: API.CRM.MOVE,
    params: { _id },
  });

  const [doc] = await axiosService.put({
    url,
    data: {
      boardUid: body.boardUid,
      columnUid: body.columnUid,
      columnOrder: body.columnOrder,
    },
    returnAsObject: true,
  });

  return doc;
}

// Start attendance for an opportunity (clears filedAt and rescheduled state)
async function startAttendance(docId, payload = {}) {
  const url = apiUrl.build({
    url: API.CRM.START_ATTENDANCE,
    params: { _id: docId },
  });
  const [result] = await axiosService.put({ url, data: payload, returnAsObject: true });
  return result;
}

// File (archive) an opportunity — SLA stops counting
async function fileOpportunity(docId) {
  const url = apiUrl.build({
    url: API.CRM.FILE,
    params: { _id: docId },
  });
  const [result] = await axiosService.put({ url, returnAsObject: true });
  return result;
}

// Reschedule an opportunity — sets attendance.rescheduledAt and attendance.rescheduledTo
async function rescheduleOpportunity(docId, payload) {
  const url = apiUrl.build({
    url: API.CRM.RESCHEDULE,
    params: { _id: docId },
  });
  const [result] = await axiosService.put({ url, data: payload, returnAsObject: true });
  return result;
}

// Generate AI description + auto-fill formData for a CRM opportunity based on lead data
// formFields = board's custom field definitions (for AI to map lead data into)
// Returns { note, formData } or { note: null, formData: null } on failure
async function describeOpportunityFromLead(lead, formFields) {
  try {
    const [result] = await axiosService.post({
      url: API.CRM.IA_DESCRIBE,
      data: {
        name: lead.name,
        interest: lead.interest,
        phone: lead.phone,
        email: lead.email,
        cpf: lead.cpf,
        customData: lead.customData,
        rawPayload: lead.rawPayload,
        // Vehicle-specific fields (common in auto leads)
        vehicleMarca: lead.vehicleMarca,
        vehicleModelo: lead.vehicleModelo,
        vehicleAno: lead.vehicleAno,
        vehicleType: lead.vehicleType,
        plate: lead.plate,
        plateDataRaw: lead.plateDataRaw,
        // Board form field definitions for AI to map
        formFields: formFields || [],
      },
      returnAsObject: true,
      timeout: 30000,
    });
    return { note: result?.note || null, formData: result?.formData || null };
  } catch {
    return { note: null, formData: null };
  }
}

// Add a follow-up entry to a CRM opportunity
async function addFollowUp(docId, payload) {
  const url = mergeUrlParamsQuery({
    url: API.CRM.ADD_TO_SET,
    params: { _id: docId, operator: '$push', path: 'followUp' },
  });
  const [result] = await axiosService.put({
    url,
    data: payload,
    headers: { 'x-resultasobject': true },
  });
  return result;
}

// Add a movement follow-up entry (stage change, transfer, deal outcome)
async function addMovementFollowUp(docId, { user, text }) {
  return addFollowUp(docId, {
    accountId: user?._id,
    accountName: user?.name || null,
    accountAvatarUrl: user?.avatar?.base64Thumb || user?.avatar?.thumbUrl || null,
    text,
    entryType: 'move',
  });
}

// *******************************************************************************
// STATUS COLORS
// *******************************************************************************
function getStatusColor(status) {
  switch (status) {
    case 'open':
      return 'blue';
    case 'won':
      return 'success';
    case 'lost':
      return 'error';
    case 'cancelled':
      return 'default';
    case 'overdue':
      return '#f50';
    default:
      return 'default';
  }
}

// *******************************************************************************
// BOARD REORDER (drag-and-drop)
// *******************************************************************************
async function reorderBoardGroups(reorderedGroups) {
  await saveBoardGroups(reorderedGroups);
}

async function reorderBoards(reorderedBoards) {
  // Merge reordered visible boards with hidden (archived/removed) boards
  const allBoards = getAllBoardsRaw();
  const visibleUids = new Set(reorderedBoards.map(b => b.uid || b._id));
  const hiddenBoards = allBoards.filter(b => !visibleUids.has(b.uid || b._id));
  await saveBoards([...reorderedBoards, ...hiddenBoards]);
}

async function reorderColumns(boardUid, reorderedColumns) {
  const boards = clone(getAllBoardsRaw());
  const idx = boards.findIndex(b => b.uid === boardUid);
  if (idx >= 0) {
    boards[idx].columns = reorderedColumns;
    await saveBoards(boards);
  }
}

async function moveBoardToGroup(boardUid, newGroupUid, newOrder) {
  const boards = clone(getAllBoardsRaw());
  const idx = boards.findIndex(b => b.uid === boardUid);

  if (idx >= 0) {
    boards[idx].boardGroupUid = newGroupUid;
    boards[idx].order = newOrder;
    await saveBoards(boards);
  }
}

// *******************************************************************************
// SLA UTILITIES (shared by KanbanCard, CardHeaderColumn2, etc.)
// *******************************************************************************

// Fallback business schedule — Mon–Fri, 08:00–18:00
const DEFAULT_BUSINESS_HOURS = {
  sunday: { enabled: false },
  monday: { enabled: true, open: '08:00', close: '18:00' },
  tuesday: { enabled: true, open: '08:00', close: '18:00' },
  wednesday: { enabled: true, open: '08:00', close: '18:00' },
  thursday: { enabled: true, open: '08:00', close: '18:00' },
  friday: { enabled: true, open: '08:00', close: '18:00' },
  saturday: { enabled: false },
};

// Returns the inAt timestamp of the current column (last history entry with no outAt).
// Falls back to createdAt (top-level or nested in _metadata) for cards that predate columnsHistory.
function getCardInAt(card) {
  const history = card?.columnsHistory;
  if (history?.length) {
    for (let i = history.length - 1; i >= 0; i--) {
      if (!history[i].outAt) return history[i].inAt;
    }
  }
  // Support both flat createdAt (kanban pipeline) and nested _metadata (document page)
  return card?.createdAt || card?._metadata?.audit?.createdAt || null;
}

// Returns wall-clock ms elapsed since the card entered the current column.
function computeElapsedMs(card) {
  const inAt = getCardInAt(card);
  if (!inAt) return 0;
  return Math.max(0, Date.now() - new Date(inAt).getTime());
}

// Returns true if the current wall-clock time falls within business hours.
function checkIsInBusinessHours(businessHours, holidays) {
  if (!businessHours) return false;
  const now = new Date();
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dateStr = now.toISOString().split('T')[0];
  const holidaySet = new Set((holidays || []).map(h => h.date));
  if (holidaySet.has(dateStr)) return false;
  const sched = businessHours[dayNames[now.getDay()]];
  if (!sched?.enabled || !sched.open || !sched.close) return false;
  const [openH, openM] = sched.open.split(':').map(Number);
  const [closeH, closeM] = sched.close.split(':').map(Number);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  return nowMinutes >= openH * 60 + openM && nowMinutes < closeH * 60 + closeM;
}

// Computes business-hours seconds elapsed since inAt.
function computeBusinessSecondsElapsed(inAt, businessHours, holidays) {
  if (!inAt || !businessHours) return 0;

  const start = new Date(inAt);
  const now = new Date();
  if (now <= start) return 0;

  const holidaySet = new Set((holidays || []).map(h => h.date));
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  let totalMs = 0;
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);

  while (cursor <= now) {
    const sched = businessHours[dayNames[cursor.getDay()]];
    const dateStr = cursor.toISOString().split('T')[0];

    if (sched?.enabled && !holidaySet.has(dateStr) && sched.open && sched.close) {
      const [openH, openM] = sched.open.split(':').map(Number);
      const [closeH, closeM] = sched.close.split(':').map(Number);

      const dayOpen = new Date(cursor);
      dayOpen.setHours(openH, openM, 0, 0);

      const dayClose = new Date(cursor);
      dayClose.setHours(closeH, closeM, 0, 0);

      const overlapStart = Math.max(start.getTime(), dayOpen.getTime());
      const overlapEnd = Math.min(now.getTime(), dayClose.getTime());

      if (overlapEnd > overlapStart) {
        totalMs += overlapEnd - overlapStart;
      }
    }

    cursor.setDate(cursor.getDate() + 1);
  }

  return Math.floor(totalMs / 1_000);
}

// Computes the exact Date when the card will reach its business-hours SLA limit.
function computeBusinessDeadline(inAt, limitMinutes, businessHours, holidays) {
  if (!inAt || !businessHours || !limitMinutes) return null;

  const start = new Date(inAt);
  const limitMs = limitMinutes * 60 * 1_000;
  const holidaySet = new Set((holidays || []).map(h => h.date));
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  let accumulated = 0;
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);

  const maxDate = new Date(start);
  maxDate.setFullYear(maxDate.getFullYear() + 1);

  while (cursor <= maxDate) {
    const sched = businessHours[dayNames[cursor.getDay()]];
    const dateStr = cursor.toISOString().split('T')[0];

    if (sched?.enabled && !holidaySet.has(dateStr) && sched.open && sched.close) {
      const [openH, openM] = sched.open.split(':').map(Number);
      const [closeH, closeM] = sched.close.split(':').map(Number);

      const dayOpen = new Date(cursor);
      dayOpen.setHours(openH, openM, 0, 0);

      const dayClose = new Date(cursor);
      dayClose.setHours(closeH, closeM, 0, 0);

      const segStart = Math.max(start.getTime(), dayOpen.getTime());
      const segEnd = dayClose.getTime();

      if (segEnd > segStart) {
        const segMs = segEnd - segStart;
        if (accumulated + segMs >= limitMs) {
          return new Date(segStart + (limitMs - accumulated));
        }
        accumulated += segMs;
      }
    }

    cursor.setDate(cursor.getDate() + 1);
  }

  return null;
}

// Returns badge color based on % SLA remaining.
function getSlaColor(pct) {
  if (pct > 0.75) return '#389e0d';
  if (pct > 0.5) return '#52c41a';
  if (pct > 0.25) return '#faad14';
  if (pct > 0) return '#fa8c16';
  return '#ff4d4f';
}

// Formats ms duration to "Dd Hh Mm" (days shown when >= 24h).
function formatDurationMs(ms) {
  const sign = ms < 0 ? '-' : '';
  const abs = Math.abs(ms);
  const totalSec = Math.floor(abs / 1_000);
  const d = Math.floor(totalSec / 86_400);
  const h = Math.floor((totalSec % 86_400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (d > 0) return `${sign}${d}d ${h}h ${m}m`;
  if (h > 0) return `${sign}${h}h ${m}m`;
  return `${sign}${m}m`;
}

// Formats seconds to "Dd Hh Mm Ss" (days shown when >= 24h).
function formatSeconds(seconds) {
  const sign = seconds < 0 ? '-' : '';
  const abs = Math.abs(Math.round(seconds));
  const d = Math.floor(abs / 86_400);
  const h = Math.floor((abs % 86_400) / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  if (d > 0) return `${sign}${d}d ${h}h ${m}m ${s}s`;
  if (h > 0) return `${sign}${h}h ${m}m ${s}s`;
  if (m > 0) return `${sign}${m}m ${s}s`;
  return `${sign}${s}s`;
}

// *******************************************************************************
// EXPORT
// *******************************************************************************
const crmService = {
  // Parameters (read)
  getBoardGroups,
  getBoards,
  getTemperatures,
  getSources,
  getBoardByUid,
  getColumnsByBoardUid,

  // Parameters (write)
  saveBoardGroups,
  saveBoards,
  saveTemperatures,
  saveSources,

  // Board group CRUD
  addBoardGroup,
  updateBoardGroupTitle,
  removeBoardGroup,

  // Board CRUD
  addBoard,
  updateBoard,
  removeBoard,
  archiveBoard,
  checkBoardHasActiveDocuments,
  getAllBoardsRaw,

  // Column CRUD
  addColumn,
  updateColumn,
  removeColumn,

  // Board reorder
  reorderBoardGroups,
  reorderBoards,
  moveBoardToGroup,

  // Column reorder
  reorderColumns,

  // API calls
  addOpportunity,
  fetchBoardView,
  moveOpportunity,
  startAttendance,
  fileOpportunity,
  rescheduleOpportunity,
  addFollowUp,
  addMovementFollowUp,
  describeOpportunityFromLead,

  // Utils
  getStatusColor,

  // SLA utilities
  DEFAULT_BUSINESS_HOURS,
  getCardInAt,
  computeElapsedMs,
  checkIsInBusinessHours,
  computeBusinessSecondsElapsed,
  computeBusinessDeadline,
  getSlaColor,
  formatDurationMs,
  formatSeconds,
};

export default crmService;
